import React, { FC, useMemo, ChangeEvent, useState, useEffect } from "react"
import { turbine } from "neql"
import { SingleLineTextInput, MultiLineTextInput } from "dash-styled"
import {
    FilterComposer,
    FilterFields
} from "../../../../../../../../../../../Common/FilterComposer/FilterComposer"
import { UnaryFilterOperator } from "../../../../../../../../../../../../Models/Client/FilterExpression"
import { QualificationMetricChooser } from "./QualificationMetricChooser"
import { MetricConstraint } from "../../../../../../../../../../../../Models/DataProgram/LabelerRestriction"
import {
    FetchState,
    getValue
} from "../../../../../../../../../../../../Models/Client/FetchState"
import { FilterOption } from "../../../../../../../../../../../Common/FilterComposer/FilterOption"
import {
    defaultRestriction,
    RestrictionFilter,
    TaskLabelerRestriction
} from "../FormFields"
import { QualificationTestChooser } from "./QualificationTestChooser"

interface LabelerRestrictionProps {
    className?: string
    qualificationMetricNamesFetch: FetchState<string[]>
    emailError?: string
    value: RestrictionFilter[]
    trainingTests: turbine.TrainingTest[]
    onChange: (value: RestrictionFilter[]) => void
    onSearchQualificationMetricNames: (search: string) => void
    onLoadMoreQualificationMetricNames?: () => void
}

function integerFilter(
    label: string,
    unit?: string
): FilterOption<number | undefined> {
    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: "",
                operator: UnaryFilterOperator.eq,
                defaultValue: undefined,
                component: ({ value, onChange }) => {
                    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                        const parsed = Number.parseInt(e.target.value, 10)

                        onChange(Number.isNaN(parsed) ? undefined : parsed)
                    }

                    return (
                        <div className="flex-grow flex items-center">
                            <SingleLineTextInput
                                className={`flex-grow ${
                                    unit === undefined ? "" : "mr-2"
                                }`}
                                testId="filter-value"
                                autoComplete="off"
                                size="sm"
                                type="number"
                                value={value === undefined ? "" : String(value)}
                                transparentBg={true}
                                onChange={handleChange}
                            />
                            {unit ?? ""}
                        </div>
                    )
                }
            }
        ]
    }
}

function integerArrayFilter(
    label: string,
    placeholder?: string
): FilterOption<number[] | undefined> {
    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: "",
                operator: UnaryFilterOperator.eq,
                defaultValue: [],
                component: ({ value, onChange }) => {
                    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value
                        const isDeleting =
                            v.slice(-1) === "," &&
                            Number.isNaN(value?.[value.length - 1])

                        onChange(
                            v === ""
                                ? undefined
                                : isDeleting
                                ? value?.slice(0, -1)
                                : v.split(/[,\s\n]+/).flatMap(vv => {
                                      if (vv === "") {
                                          return [NaN]
                                      }
                                      const decoded = Number.parseInt(vv, 10)

                                      return Number.isNaN(decoded)
                                          ? []
                                          : [decoded]
                                  })
                        )
                    }

                    return (
                        <SingleLineTextInput
                            className="flex-grow"
                            transparentBg={true}
                            testId="filter-value"
                            autoComplete="off"
                            placeholder={placeholder}
                            size="sm"
                            value={
                                value
                                    ?.map(v =>
                                        Number.isNaN(v) ? "" : String(v)
                                    )
                                    .join(", ") ?? ""
                            }
                            onChange={handleChange}
                        />
                    )
                }
            }
        ]
    }
}

function stringArrayFilter(
    label: string,
    placeholder?: string,
    errorText?: string
): FilterOption<string[] | undefined> {
    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: "",
                operator: UnaryFilterOperator.eq,
                defaultValue: [],
                component: ({ value, onChange }) => {
                    const handleChange = (
                        e: ChangeEvent<HTMLTextAreaElement>
                    ) => {
                        const v = e.target.value
                        const isDeleting =
                            v.slice(-1) === "," &&
                            value?.[value.length - 1] === ""

                        onChange(
                            v === ""
                                ? undefined
                                : isDeleting
                                ? value?.slice(0, -1)
                                : v.split(/[,\s\n]+/).map(vv => vv.trim())
                        )
                    }

                    return (
                        <MultiLineTextInput
                            className="flex-grow"
                            size="sm"
                            variant="bordered"
                            testId="filter-value"
                            newlineExpanding={true}
                            transparentBg={true}
                            errorText={errorText}
                            inputProps={{
                                value: value?.join(", ") ?? "",
                                placeholder,
                                onChange: handleChange
                            }}
                        />
                    )
                }
            }
        ]
    }
}

function qualificationFilter(
    label: string,
    metricNamesFetch: FetchState<string[]>,
    onSearchChoices: (search: string) => void,
    onLoadMoreChoices?: () => void
): FilterOption<MetricConstraint[] | undefined> {
    return {
        label,
        allowMultiple: true,
        predicates: [
            {
                label: "",
                defaultValue: [
                    {
                        name: getValue(metricNamesFetch)?.[0] ?? "",
                        operator: "GREATER_THAN",
                        value: 0
                    }
                ],
                operator: UnaryFilterOperator.eq,
                component: props => (
                    <QualificationMetricChooser
                        choicesFetch={metricNamesFetch}
                        onSearchChoices={onSearchChoices}
                        onLoadMoreChoices={onLoadMoreChoices}
                        {...props}
                    />
                )
            }
        ]
    }
}

function qualificationTestFilter(
    label: string,
    trainingTests: turbine.TrainingTest[]
): FilterOption<string | undefined> {
    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: "",
                defaultValue: trainingTests[0]?.trainingId ?? "",
                operator: UnaryFilterOperator.eq,
                component: props => (
                    <QualificationTestChooser
                        trainingTests={trainingTests}
                        {...props}
                    />
                )
            }
        ]
    }
}

export const LabelerRestriction: FC<LabelerRestrictionProps> = ({
    className,
    qualificationMetricNamesFetch,
    emailError,
    value,
    trainingTests,
    onChange,
    onSearchQualificationMetricNames,
    onLoadMoreQualificationMetricNames
}) => {
    const [qualificationName, setQualificationName] =
        useState<string | undefined>(undefined)
    const fields = useMemo<FilterFields<TaskLabelerRestriction>>(
        () => ({
            id: integerFilter("Have ID"),
            emails: stringArrayFilter(
                "Have email",
                "e.g. name1@company.com, name2@company.com",
                emailError
            ),
            groups: stringArrayFilter("Be in group", "e.g. GROUP_1, GROUP_2"),
            excluded: integerArrayFilter("Not have ID", "e.g. 1111, 2222"),
            excludedGroups: stringArrayFilter(
                "Not be in group",
                "e.g. GROUP_1, GROUP_2"
            ),
            metrics: qualificationFilter(
                "Have passed qualifier",
                qualificationMetricNamesFetch,
                setQualificationName,
                onLoadMoreQualificationMetricNames
            ),
            ...(trainingTests.length > 0
                ? {
                      qualifierTestId: qualificationTestFilter(
                          "Have passed training",
                          trainingTests
                      )
                  }
                : {})
        }),
        [
            emailError,
            qualificationMetricNamesFetch,
            onLoadMoreQualificationMetricNames,
            trainingTests
        ]
    )

    useEffect(() => {
        if (qualificationName !== undefined) {
            onSearchQualificationMetricNames(qualificationName)
        }
    }, [onSearchQualificationMetricNames, qualificationName])

    return (
        <FilterComposer
            className={className}
            addFirstText="Add restriction"
            addAnotherText="Add restriction"
            compact={true}
            value={value}
            fields={fields}
            defaultFilter={defaultRestriction}
            onChange={onChange}
        />
    )
}
